import {
  Box,
  Button,
  Checkbox,
  Divider,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { AppIcons } from "constants/icons";
import { Header } from "build-ts/metadata";
import { DarwinHeader } from "build-ts/common";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { buildWorkflowTag } from "modules/chat/utils/chatUtils";
import AnalystMemoryFactsContainer from "modules/analyst/containers/AnalystMemoryFacts";
import UploadTableModal from "../containers/UploadTableModal";

const ACCEPTED_FILE_TYPES = ".csv,.png,.jpg,.jpeg,.pdf,.docx";

interface MessageInputAddMenuProps {
  sessionHeader: DarwinHeader | undefined;
  analyst: Header | undefined;
  chatId: string;
  onSelectAnalysis?: (message: string) => void;
  // ── Container-provided state ─────────────────────────────────────────────
  jobs?: ScheduledJobProto[];
  isFetchingJobs?: boolean;
  // ── Container-provided callbacks ─────────────────────────────────────────
  onFetchJobs?: () => Promise<ScheduledJobProto[]>;
  // Learn modal close handler - refreshes the system prompt
  onLearnModalClose?: () => void;
  onCsvFileSelected?: (files: File[]) => void;
  onNonCsvFileSelected?: (files: File[]) => void;
}

const MessageInputAddMenu: React.FC<MessageInputAddMenuProps> = ({
  analyst,
  onSelectAnalysis,
  jobs = [],
  isFetchingJobs = false,
  onFetchJobs,
  onLearnModalClose,
  onCsvFileSelected,
  onNonCsvFileSelected,
}) => {
  // ── File input ref — unified upload (CSV + images + PDF/DOCX) ───────────
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Upload Table modal state ──────────────────────────────────────────────
  const { isOpen: isUploadTableOpen, onOpen: onUploadTableOpen, onClose: onUploadTableClose } = useDisclosure();

  // ── CSV file preview queue (Upload CSV flow) ──────────────────────────────
  // Files selected via the file picker are queued here. Each is previewed one
  // at a time through UploadTableModal before being staged in the input area.
  // `csvQueueKey` increments on each advance so React re-mounts the modal and
  // triggers the auto-parse useEffect for the next file.
  const [csvPreviewQueue, setCsvPreviewQueue] = useState<File[]>([]);
  const [csvQueueKey, setCsvQueueKey] = useState(0);

  // ── Learn modal state ─────────────────────────────────────────────────────
  const { isOpen: isLearnOpen, onOpen: onLearnOpen, onClose: onLearnClose } = useDisclosure();
  const [hasLearnChanged, setHasLearnChanged] = useState(false);

  const handleLearnClose = useCallback(() => {
    onLearnClose();
    // Refreshes the system prompt if the learn modal has been closed
    // and the user has made any changes to the memory facts
    if (hasLearnChanged) {
      onLearnModalClose?.();
    }
    setHasLearnChanged(false);
  }, [onLearnClose, hasLearnChanged, onLearnModalClose]);

  // ── Analysis modal state ──────────────────────────────────────────────────
  const { isOpen: isAnalysisOpen, onOpen: onAnalysisOpen, onClose: onAnalysisClose } = useDisclosure();
  const [jobSearch, setJobSearch] = useState("");
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());

  const filteredJobs = useMemo(() => {
    const term = jobSearch.toLowerCase().trim();
    if (!term) return jobs;
    return jobs.filter(j => j.header?.name?.toLowerCase().includes(term));
  }, [jobs, jobSearch]);

  // ── Learn handlers ────────────────────────────────────────────────────────

  const handleLearnClick = useCallback(() => {
    if (!analyst?.id) return;
    setHasLearnChanged(false);
    onLearnOpen();
  }, [analyst?.id, onLearnOpen]);

  // ── Analysis handlers ─────────────────────────────────────────────────────

  const handleAnalysisClick = useCallback(async () => {
    if (!analyst?.id) return;
    setSelectedJobIds(new Set());
    await onFetchJobs?.();
    setJobSearch("");
    onAnalysisOpen();
  }, [analyst?.id, onFetchJobs, onAnalysisOpen]);

  const handleJobToggle = useCallback((jobId: string) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  }, []);

  const handleAddSelected = useCallback(() => {
    const selected = jobs
      .filter(j => j.type === ScheduledJobType.SCENARIO_JOB)
      .filter(j => j.header?.id && selectedJobIds.has(j.header.id));
    if (selected.length === 0) return;
    const tags = selected
      .map(j => {
        const lookupKey = j.scheduled_analysis?.darwin_name || j.header?.id || "";
        const displayName = j.header?.name?.trim() || "Untitled";
        return buildWorkflowTag(lookupKey, displayName);
      })
      .join(" ");
    onSelectAnalysis?.(tags);
    onAnalysisClose();
    setSelectedJobIds(new Set());
  }, [jobs, selectedJobIds, onSelectAnalysis, onAnalysisClose]);

  return (
    <>
      <Portal>
        <MenuList minW="240px" py={0} zIndex={1500}>
          {/* Learn */}
          <MenuItem
            py={3}
            px={3}
            _hover={{ bg: "purple.50" }}
            _focus={{ bg: "purple.50" }}
            onClick={handleLearnClick}
            isDisabled={!analyst?.id}
          >
            <VStack align="stretch" spacing={1} w="100%">
              <HStack spacing={2}>
                <Icon as={AppIcons.analystMemory} boxSize={5} color="purple.600" />
                <Text fontWeight="600" fontSize="sm" m={0}>
                  Learn
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600" m={0}>
                Add a fact to analyst's memory
              </Text>
            </VStack>
          </MenuItem>

          {/* Analysis */}
          <MenuItem
            py={3}
            px={3}
            _hover={{ bg: "purple.50" }}
            _focus={{ bg: "purple.50" }}
            onClick={handleAnalysisClick}
            isDisabled={isFetchingJobs || !analyst?.id}
          >
            <VStack align="stretch" spacing={1} w="100%">
              <HStack spacing={2}>
                {isFetchingJobs ? (
                  <Spinner size="xs" color="purple.600" />
                ) : (
                  <Icon as={AppIcons.workflow} boxSize={5} color="purple.600" />
                )}
                <Text fontWeight="600" fontSize="sm" m={0}>
                  Workflow
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600" m={0}>
                Import an workflow into context
              </Text>
            </VStack>
          </MenuItem>

          {/* Add photos and files */}
          <MenuItem
            py={3}
            px={3}
            _hover={{ bg: "purple.50" }}
            _focus={{ bg: "purple.50" }}
            onClick={() => fileInputRef.current?.click()}
          >
            <VStack align="stretch" spacing={1} w="100%">
              <HStack spacing={2}>
                <Icon as={AppIcons.attachment} boxSize={5} color="purple.600" />
                <Text fontWeight="600" fontSize="sm" m={0}>
                  Add photos and files
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600" m={0}>
                Upload CSV, images (PNG/JPEG), PDF, or Word docs
              </Text>
            </VStack>
          </MenuItem>

          {/* Upload Table — paste from Excel, Google Sheets, or CSV */}
          <MenuItem py={3} px={3} _hover={{ bg: "purple.50" }} _focus={{ bg: "purple.50" }} onClick={onUploadTableOpen}>
            <VStack align="stretch" spacing={1} w="100%">
              <HStack spacing={2}>
                <Icon as={AppIcons.dataframe} boxSize={5} color="purple.600" />
                <Text fontWeight="600" fontSize="sm" m={0}>
                  Paste Table
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600" m={0}>
                Paste from Excel, Google Sheets, or CSV
              </Text>
            </VStack>
          </MenuItem>
        </MenuList>
      </Portal>

      {/* Hidden input — CSV, images, PDF, and DOCX; CSVs support multiple selection */}
      <Input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        multiple
        display="none"
        onChange={e => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          if (files.length === 0) return;

          const csvFiles = files.filter(f => f.name.toLowerCase().endsWith(".csv"));
          const nonCsvFiles = files.filter(f => !f.name.toLowerCase().endsWith(".csv"));

          if (csvFiles.length > 0) {
            // Route all CSV files through the preview queue
            setCsvPreviewQueue(csvFiles);
            setCsvQueueKey(k => k + 1);
            onUploadTableOpen();
          } else if (nonCsvFiles.length > 0) {
            // Non-CSV files (images, PDF, DOCX) are staged directly — no modal
            onNonCsvFileSelected?.(nonCsvFiles);
          }

          e.target.value = "";
        }}
      />

      {/* ── Learn modal ───────────────────────────────────────────────────── */}
      <Modal isOpen={isLearnOpen} onClose={handleLearnClose} size="3xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader pb={2}>
            <HStack spacing={3}>
              <Icon as={AppIcons.analystMemory} boxSize={8} color="purple.600" />
              <Text m={0} fontSize="xl" fontWeight="600">
                {analyst?.name}'s Memory
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0} overflow="hidden">
            <Box h="60vh" overflow="hidden">
              <AnalystMemoryFactsContainer
                analystId={analyst?.id}
                isOpen={isLearnOpen}
                onChanged={() => setHasLearnChanged(true)}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* ── Upload Table modal ───────────────────────────────────────────── */}
      <UploadTableModal
        key={csvQueueKey}
        isOpen={isUploadTableOpen}
        onClose={() => {
          setCsvPreviewQueue([]);
          onUploadTableClose();
        }}
        onCsvFileSelected={files => {
          onCsvFileSelected?.(files);
          // Advance to the next file in the queue, or close if the queue is empty.
          setCsvPreviewQueue(prev => {
            const remaining = prev.slice(1);
            if (remaining.length > 0) {
              setCsvQueueKey(k => k + 1);
            } else {
              onUploadTableClose();
            }
            return remaining;
          });
        }}
        initialCsvFile={csvPreviewQueue.length > 0 ? csvPreviewQueue[0] : null}
      />

      {/* ── Workflow modal ────────────────────────────────────────────────── */}
      <Modal
        isOpen={isAnalysisOpen}
        onClose={onAnalysisClose}
        size="3xl"
        isCentered
        scrollBehavior="inside"
        returnFocusOnClose={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={AppIcons.workflow} boxSize={7} color="purple.600" />
              <Text m={0}>Select an Workflow</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody px={4} py={0}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600" mb={0}>
                This workflow will be imported into this conversation.
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={AppIcons.search} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search workflows…"
                  value={jobSearch}
                  onChange={e => setJobSearch(e.target.value)}
                  bg="gray.50"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                    bg: "white",
                  }}
                />
              </InputGroup>
              {isFetchingJobs ? (
                <HStack justify="center" py={6}>
                  <Spinner size="md" color="purple.500" />
                </HStack>
              ) : filteredJobs.length === 0 ? (
                <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
                  {jobSearch ? "No workflows match your search." : "No workflows found."}
                </Text>
              ) : (
                <VStack spacing={0} align="stretch" divider={<Divider />}>
                  {filteredJobs.map((job, idx) => {
                    const jobId = job.header?.id ?? String(idx);
                    const isChecked = selectedJobIds.has(jobId);
                    const createdDate = job.header?.created_date_ms
                      ? new Date(job.header.created_date_ms).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : null;
                    return (
                      <Box
                        key={jobId}
                        px={3}
                        py={3}
                        cursor="pointer"
                        borderRadius="md"
                        bg={isChecked ? "purple.50" : undefined}
                        _hover={{ bg: isChecked ? "purple.100" : "gray.50" }}
                        onClick={() => handleJobToggle(jobId)}
                      >
                        <HStack spacing={3} align="flex-start">
                          <Checkbox
                            isChecked={isChecked}
                            colorScheme="purple"
                            mt="2px"
                            flexShrink={0}
                            onChange={() => handleJobToggle(jobId)}
                            onClick={e => e.stopPropagation()}
                          />
                          <Box flex={1} minW={0}>
                            <HStack justify="space-between" align="flex-start">
                              <Text fontSize="sm" fontWeight="600" color="gray.800" m={0}>
                                {job.header?.name || "Untitled"}
                              </Text>
                              {job.header?.has_active_schedule && (
                                <HStack spacing={1} flexShrink={0}>
                                  <Box w={2} h={2} borderRadius="full" bg="green.400" />
                                  <Text fontSize="xs" fontWeight="600" color="green.600" m={0}>
                                    Live
                                  </Text>
                                </HStack>
                              )}
                            </HStack>
                            {job.header?.description && (
                              <Text fontSize="xs" color="gray.500" m={0} mt={0.5} noOfLines={1}>
                                {job.header.description}
                              </Text>
                            )}
                            <HStack spacing={2} mt={1.5} flexWrap="wrap">
                              {job.header?.author_name && (
                                <Text fontSize="xs" color="gray.400" m={0}>
                                  By {job.header.author_name}
                                </Text>
                              )}
                              {job.header?.author_name && createdDate && (
                                <Text fontSize="xs" color="gray.300" m={0}>
                                  ·
                                </Text>
                              )}
                              {createdDate && (
                                <Text fontSize="xs" color="gray.400" m={0}>
                                  {createdDate}
                                </Text>
                              )}
                            </HStack>
                          </Box>
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Text fontSize="sm" color="gray.500" mr="auto" mb={0}>
              {selectedJobIds.size > 0 ? `${selectedJobIds.size} selected` : ""}
            </Text>
            <Button colorScheme="purple" isDisabled={selectedJobIds.size === 0} onClick={handleAddSelected}>
              Add to chat
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MessageInputAddMenu;
